import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BadgeIcon from "@mui/icons-material/Badge";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import DI from "./utility/DI";
import { AppContext } from "../App";

const Settings = (props) => {
  const navigate = useNavigate();
  const { state, setState } = useContext(AppContext);
  const {
    mode,
    setMode,
    di: { session, POST, urls, error, success, logout },
  } = props;

  // Change password state
  const [pwForm, setPwForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [pwLoading, setPwLoading] = useState(false);
  const [showPwSection, setShowPwSection] = useState(false);

  // WhatsApp edit state
  const [whatsapp, setWhatsapp] = useState(session?.whatsapp_number || "");
  const [waLoading, setWaLoading] = useState(false);
  const [isEditingWa, setIsEditingWa] = useState(false);

  const isDark = mode === "dark";

  const handleThemeToggle = () => {
    setMode(isDark ? "light" : "dark");
  };

  const handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = pwForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      return error("All fields are required");
    }
    if (newPassword.length < 6) {
      return error("Password must be at least 6 characters");
    }
    if (newPassword !== confirmPassword) {
      return error("Passwords do not match");
    }

    setPwLoading(true);
    try {
      const res = await POST(urls.post.change_password, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (res?.success) {
        success(res.message || "Password changed successfully");
        setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setShowPwSection(false);
      } else {
        error(res?.message || "Failed to change password");
      }
    } catch (e) {
      error("Failed to change password");
    }
    setPwLoading(false);
  };

  const handleWhatsappUpdate = async () => {
    if (!whatsapp) return error("WhatsApp number is required");
    setWaLoading(true);
    try {
      // Assuming a generic update_profile API or a specific one
      // I'll create backend/api/update_profile.php if it doesn't exist
      const res = await POST(urls.post.update_profile, {
        whatsapp_number: whatsapp,
      });
      if (res?.success) {
        success("WhatsApp number updated");
        // Update session in context
        setState(prev => ({
          ...prev,
          session: { ...prev.session, whatsapp_number: whatsapp }
        }));
        setIsEditingWa(false);
      } else error(res?.message || "Update failed");
    } catch (e) {
      error("Update failed");
    }
    setWaLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const d = new Date(dateString.replace(" ", "T"));
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const initials = session
    ? `${(session.firstname || "")[0] || ""}${(session.lastname || "")[0] || ""}`.toUpperCase()
    : "?";

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", pb: 4 }}>
      {/* Header with back button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        <IconButton onClick={() => navigate(-1)} sx={{ color: "text.primary" }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
      </Box>

      {/* ===== PROFILE CARD ===== */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, position: "relative", zIndex: 1 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              fontSize: 22,
              fontWeight: 700,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, textTransform: "capitalize", lineHeight: 1.2 }}
              noWrap
            >
              {session?.firstname} {session?.lastname}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }} noWrap>
              {session?.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", my: 2 }} />

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <BadgeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              ID: <strong style={{ opacity: 1 }}>{session?.id}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <CalendarTodayIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Joined: <strong style={{ opacity: 1 }}>{formatDate(session?.created_at)}</strong>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ===== THEME TOGGLE ===== */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isDark ? (
              <Brightness7Icon sx={{ color: "warning.main" }} />
            ) : (
              <Brightness4Icon sx={{ color: "text.secondary" }} />
            )}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Dark Mode
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isDark ? "Currently using dark theme" : "Currently using light theme"}
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={isDark}
            onChange={handleThemeToggle}
            color="primary"
          />
        </Box>
      </Box>

      {/* ===== CHANGE PASSWORD ===== */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
            cursor: "pointer",
          }}
          onClick={() => setShowPwSection(!showPwSection)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LockIcon sx={{ color: "text.secondary" }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Change Password
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Update your account password
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: "primary.main", fontSize: 20, fontWeight: 300 }}>
            {showPwSection ? "−" : "+"}
          </Typography>
        </Box>

        {showPwSection && (
          <Box sx={{ px: 2.5, pb: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
            <Divider />
            <TextField
              size="small"
              type="password"
              label="Current Password"
              value={pwForm.currentPassword}
              onChange={(e) =>
                setPwForm({ ...pwForm, currentPassword: e.target.value })
              }
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              label="New Password"
              value={pwForm.newPassword}
              onChange={(e) =>
                setPwForm({ ...pwForm, newPassword: e.target.value })
              }
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              label="Confirm New Password"
              value={pwForm.confirmPassword}
              onChange={(e) =>
                setPwForm({ ...pwForm, confirmPassword: e.target.value })
              }
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handlePasswordChange}
              disabled={pwLoading}
              startIcon={
                pwLoading ? <CircularProgress size={16} color="inherit" /> : <LockIcon />
              }
              sx={{ alignSelf: "flex-end" }}
            >
              Update Password
            </Button>
          </Box>
        )}
      </Box>

      {/* ===== ACCOUNT INFO ===== */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
            Account Details
          </Typography>
        </Box>
        <Divider />
        {[
          { icon: <PersonIcon />, label: "Name", value: `${session?.firstname || ""} ${session?.lastname || ""}` },
          { icon: <EmailIcon />, label: "Email", value: session?.email },
          { icon: <BadgeIcon />, label: "User ID", value: session?.id },
          { icon: <WhatsAppIcon />, label: "WhatsApp Number", value: session?.whatsapp_number || "Not provided" },
          { icon: <CalendarTodayIcon />, label: "Member Since", value: formatDate(session?.created_at) },
        ].map((item, i) => (
          <Box key={i}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                py: 1.5,
              }}
            >
              <Box sx={{ color: "text.secondary", display: "flex" }}>{item.icon}</Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                  {item.label === "WhatsApp Number" ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      {isEditingWa ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", mt: 0.5 }}>
                          <TextField
                            size="small"
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            sx={{ flex: 1 }}
                            autoFocus
                          />
                          <IconButton size="small" color="success" onClick={handleWhatsappUpdate} disabled={waLoading}>
                            {waLoading ? <CircularProgress size={20} /> : <CheckIcon />}
                          </IconButton>
                          <IconButton size="small" onClick={() => { setIsEditingWa(false); setWhatsapp(session?.whatsapp_number || ""); }}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.value || "Not provided"}
                          </Typography>
                          <Button size="small" sx={{ textTransform: "none", fontSize: 11 }} onClick={() => setIsEditingWa(true)}>Edit</Button>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, textTransform: (item.label === "Name") ? "capitalize" : "none" }}
                      noWrap
                    >
                      {item.value || "N/A"}
                    </Typography>
                  )}
              </Box>
            </Box>
            {i < 4 && <Divider />}
          </Box>
        ))}
      </Box>

      {/* ===== LOGOUT BUTTON ===== */}
      <Box sx={{ mt: 4, px: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
              bgcolor: "error.lighter",
            },
          }}
        >
          Logout from Account
        </Button>
      </Box>
    </Box>
  );
};

export default DI(Settings);
